import { createFileRoute, getRouteApi, redirect } from "@tanstack/react-router";

import { useViewer } from "../data/hooks.ts";
import { viewerQuery } from "../data/viewer.ts";
import { settleAccountLanguage } from "../language/account-language.ts";
import { AppLayout } from "../shell/app-layout.tsx";
import { ShellPending } from "../shell/shell-pending.tsx";
import { NoAccessPage } from "../shell/status-pages.tsx";

import type { ReactElement } from "react";

const route = getRouteApi("/_app");

const App = (): ReactElement => {
  const { user } = route.useRouteContext();
  const viewer = useViewer();
  return viewer === null ? <NoAccessPage /> : <AppLayout viewer={viewer} user={user} />;
};

export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context }) => {
    const user = await context.data.auth.currentUser();
    if (user === null) {
      const reason = context.data.auth.lastSignOutReason();
      throw redirect({ to: "/sign-in", search: reason === "security" ? { reason } : {} });
    }
    if (!user.isComplete) {
      throw redirect({ to: "/sign-in/verify" });
    }
    await context.data.cache.restore(user.id);
    context.data.keeper.start();
    return { user };
  },
  loader: async ({ context }) => {
    settleAccountLanguage(context.user);
    const viewer = await context.data.cache.queryClient.query({
      ...viewerQuery(context.data.client),
      staleTime: "static",
    });
    if (viewer !== null) {
      context.data.live.watch(viewer.householdId, context.user.id);
    }
    return viewer;
  },
  pendingComponent: ShellPending,
  component: App,
});
